import { useCallback, useEffect, useRef, useState } from 'react';
import type { HeatmapCell as HeatmapCellData } from '../../../types/weekOverview';
import {
  HEATMAP_CLOSED_CELL_TEXT,
  HEATMAP_TIER_COLORS,
  HEATMAP_TOOLTIP_ID
} from '../../../constants/weekOverviewConstants';
import { getHeatmapTier } from '../../../services/heatmapService';
import type { HeatmapNavKey } from '../../../services/heatmapNavigationService';
import { isHeatmapNavKey } from '../../../services/heatmapNavigationService';
import styles from './WeekHeatmap.module.css';

// What a cell needs to take part in the grid's arrow-key navigation. Supplied
// together or not at all: a cell without them keeps its default tab behaviour.
export interface HeatmapCellNavProps {
  cellId: string;
  isTabStop: boolean;
  onNavigate: (cellId: string, key: HeatmapNavKey) => void;
  onCellFocus: (cellId: string) => void;
}

interface HeatmapCellProps extends Partial<HeatmapCellNavProps> {
  cell: HeatmapCellData;
  label?: string;
  isChild?: boolean;
  onClick?: () => void;
  onHover?: (cell: HeatmapCellData | null, position: { x: number; y: number } | null) => void;
}

export const HeatmapCell = ({
  cell,
  label,
  isChild = false,
  cellId,
  isTabStop = false,
  onClick,
  onHover,
  onNavigate,
  onCellFocus
}: HeatmapCellProps) => {
  const tier = cell.isClosed ? 'closed' : getHeatmapTier(cell.utilizationPercent, cell.isClosed);
  const colors = HEATMAP_TIER_COLORS[tier];
  const isActivatable = !cell.isClosed && Boolean(onClick);
  // Closed days and week totals activate nothing, but their tooltips are worth
  // reading, so inside the grid they are focusable buttons that say they are
  // inert — never focusable divs.
  const isNavigable = cellId !== undefined;
  // Of the cells in the grid, only the one holding the tab stop is a Tab target;
  // the arrows reach the rest.
  const rovingTabIndex = isTabStop ? 0 : -1;
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const plainCellRef = useRef<HTMLDivElement>(null);

  const showTooltip = useCallback(
    (element: HTMLElement) => {
      if (onHover) {
        const rect = element.getBoundingClientRect();
        onHover(cell, { x: rect.left + rect.width / 2, y: rect.top });
      }
    },
    [cell, onHover]
  );

  const hideTooltip = useCallback(() => {
    if (onHover) onHover(null, null);
  }, [onHover]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      showTooltip(e.currentTarget);
    },
    [showTooltip]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsTooltipVisible(true);
      showTooltip(e.currentTarget);
      // Focus is the single source of truth for where the roving tab stop sits,
      // however it got here — Tab, an arrow key, or a click.
      if (cellId !== undefined && onCellFocus) onCellFocus(cellId);
    },
    [cellId, onCellFocus, showTooltip]
  );

  const handleBlur = useCallback(() => {
    setIsTooltipVisible(false);
    hideTooltip();
  }, [hideTooltip]);

  // The tooltip can cover neighbouring cells, so keyboard users need to dismiss
  // it without leaving the cell (WCAG 1.4.13). Arrow/Home/End move focus to a
  // neighbouring cell instead of scrolling the page, so the grid is one tab stop
  // rather than one per cell.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Escape' && isTooltipVisible) {
        e.stopPropagation();
        setIsTooltipVisible(false);
        hideTooltip();
        return;
      }
      if (cellId !== undefined && onNavigate && isHeatmapNavKey(e.key)) {
        e.preventDefault();
        onNavigate(cellId, e.key);
      }
    },
    [cellId, hideTooltip, isTooltipVisible, onNavigate]
  );

  // A cell outside the grid navigation with nothing to activate keeps its
  // tooltip as a mouse-only affordance. The listeners are attached imperatively
  // because hover handlers written into the JSX make a plain <div> read as an
  // interactive element that lacks keyboard support (Sonar S6848) — the same
  // approach UI/Tooltip takes for its trigger.
  useEffect(() => {
    const element = plainCellRef.current;
    if (!element) return;
    const handleEnter = (): void => showTooltip(element);
    element.addEventListener('mouseenter', handleEnter);
    element.addEventListener('mouseleave', hideTooltip);
    return () => {
      element.removeEventListener('mouseenter', handleEnter);
      element.removeEventListener('mouseleave', hideTooltip);
    };
  }, [hideTooltip, showTooltip]);

  const className = [
    styles.cell,
    isChild ? styles.cellChild : '',
    isActivatable ? styles.cellInteractive : '',
    isNavigable ? styles.cellFocusable : ''
  ].join(' ');
  const cellStyle = {
    background: colors.background,
    color: colors.text,
    borderColor: colors.border
  };
  const text = cell.isClosed ? HEATMAP_CLOSED_CELL_TEXT : `${cell.utilizationPercent}%`;

  if (!isActivatable && !isNavigable) {
    return (
      <div ref={plainCellRef} className={className} style={cellStyle}>
        {text}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      style={cellStyle}
      tabIndex={isNavigable ? rovingTabIndex : undefined}
      data-heatmap-cell-id={cellId}
      aria-label={label}
      aria-disabled={isActivatable ? undefined : true}
      aria-describedby={isTooltipVisible ? HEATMAP_TOOLTIP_ID : undefined}
      onClick={isActivatable ? onClick : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={hideTooltip}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {text}
    </button>
  );
};
